
import React, { useEffect, useState } from 'react';
import { Form, message, Modal, notification, Progress } from 'antd';
import dayjs from 'dayjs';
import axios from 'axios';
import AddLinePr from '../pages/inventory/add_line_pr';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import useSWR from 'swr';

interface TableColumn {
    title: string;
    dataIndex: string;
}

interface PurchaseRequestDetailData {
    id: number;
    status: string | number;
    pr_no: string;
    pr_date: string;
    item_group: string;
    brand: string;
    profit_center: string;
    dealer_rep_code: string;
    order_type: string;
    expected_arrival: string;
    remark: string;
    created_by: string;
    items: any[];
}

const fetcher = (url: string) => axios.get(url).then(res => res.data);

const withPurchaseRequestLogic = <P extends object>(WrappedComponent: React.ComponentType<P>) => {
    return (props: P) => {
        const [form] = Form.useForm();
        const [isLoading, setIsLoading] = useState<boolean>(true);
        const [dealerOptions, setDealerOptions] = useState<{ value: string; label: string }[]>([]);
        const [isModalVisible, setIsModalVisible] = useState(false);
        const [items, setItems] = useState<any[]>([]);
        const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
        const showModal = () => setIsModalVisible(true);
        const handleCancel = () => {
            setIsModalVisible(false);
            setEditItem(null);
        };
        const [editItem, setEditItem] = useState<any>(null);
        const [isSaving, setIsSaving] = useState(false);
        const { userData } = useAuth();
        const { id: prIdFromUrl } = useParams<{ id?: string }>();
        const navigate = useNavigate();
        const [currentPrId, setCurrentPrId] = useState<string | undefined>(undefined);
        const isEditMode = !!currentPrId;
        const dealerRepCodeValue = Form.useWatch('dealer_rep_code', form);
        const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
        const [isPrinting, setIsPrinting] = useState<boolean>(false);
        const [autosaveStatus, setAutosaveStatus] = useState<'idle' | 'processing' | 'error' | 'stopped'>('stopped');
        const [hasInitialSaveOccurred, setHasInitialSaveOccurred] = useState(false);
        const [lastSavedValues, setLastSavedValues] = useState<any>(null);
        const [lastSavedItems, setLastSavedItems] = useState<any[]>([]);
        const allFormValues = Form.useWatch([], form);
        const statusWatchedValue = Form.useWatch('status', form);

        const { data: brandData, error: brandError } = useSWR<{ value: string; label: string }[]>(`${process.env.REACT_APP_BACKEND_URL}/api/brands`, fetcher);
        useEffect(() => {
            if (brandError) {
                console.error('Gagal mengambil data brand:', brandError);
                notification.error({ message: 'Error', description: 'Gagal memuat data Brand', duration: 3 });
            }
        }, [brandError]);

        const { data: profitCenterData, error: profitCenterError, isLoading: isLoadingProfitCenters } = useSWR<{ value: string; label: string }[]>(`${process.env.REACT_APP_BACKEND_URL}/api/profit-centers`, fetcher);

        useEffect(() => {
            if (profitCenterError) {
                console.error('Gagal mengambil data Profit Center:', profitCenterError);
                notification.error({ message: 'Error', description: 'Gagal memuat data Profit Center', duration: 3 });
            }
        }, [profitCenterError]);

        const { data: orderTypeData, error: orderTypeError, isLoading: isLoadingOrderTypes } = useSWR<{ value: string; label: string }[]>(`${process.env.REACT_APP_BACKEND_URL}/api/order-types`, fetcher);
        useEffect(() => {
            if (orderTypeError) {
                console.error('Gagal mengambil data Order Type:', orderTypeError);
                notification.error({ message: 'Error', description: 'Gagal memuat data Order Type', duration: 3 });
            }
        }, [orderTypeError]);

        const handleItemGroupChange = async (value: string) => {
            form.setFieldsValue({ item_group: value });
            const currentPrNo = form.getFieldValue('pr_no');
            if (value && value.trim() !== "" && (!currentPrNo || currentPrNo === '')) {
                try {
                    const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/generate-pr-no`);
                    const { pr_no } = response.data;
                    form.setFieldsValue({ pr_no: pr_no });
                    if (!currentPrId) {
                        await handleSave(false, 1, true);
                        setHasInitialSaveOccurred(true);
                    }
                } catch (error) {
                    console.error('Gagal menghasilkan nomor PR:', error);
                    notification.error({ message: 'Error', description: 'Gagal menghasilkan nomor PR otomatis.', duration: 3 });
                }
            } else if (!value) {
                form.setFieldsValue({ pr_no: '' });
            }
        };

        const fetchPurchaseRequestDetails = async (id: string) => {
            setIsLoading(true);
            setIsSubmitted(false);
            try {
                const response = await axios.get<PurchaseRequestDetailData>(`${process.env.REACT_APP_BACKEND_URL}/api/purchase-requests/${id}`);
                const data = response.data;
                let statusDisplayValue: string | number;
                if (data.status == 1) {
                    statusDisplayValue = 'Draft';
                } else if (data.status == 7) {
                    statusDisplayValue = 'Ready';
                    setIsSubmitted(true);
                } else if (data.status == 5) {
                    statusDisplayValue = 'Canceled';
                    setIsSubmitted(true);
                } else if (data.status == 6) {
                    statusDisplayValue = 'Closed';
                    setIsSubmitted(true);
                } else if (data.status == 4) {
                    statusDisplayValue = 'Close / Complete';
                    setIsSubmitted(true);
                } else {
                    statusDisplayValue = data.status;
                }

                const formattedData = {
                    ...data,
                    status: statusDisplayValue,
                    pr_date: data.pr_date ? dayjs(data.pr_date, 'YYYY-MM-DD') : null,
                    expected_arrival: data.expected_arrival ? dayjs(data.expected_arrival, 'YYYY-MM-DD HH:mm') : null,
                };
                form.setFieldsValue(formattedData);
                const itemsWithKeys = data.items.map(item => ({ ...item, key: item.item_code || item.id }));
                setItems(itemsWithKeys);


                setLastSavedValues(formattedData);
                setLastSavedItems(itemsWithKeys);
                setHasInitialSaveOccurred(true);

            } catch (error) {
                console.error('Gagal mengambil detail purchase request:', error);
                notification.error({ message: 'Error', description: 'Gagal memuat data Purchase Request', duration: 3 });
            } finally {
                setIsLoading(false);
            }
        };

        const handleSave = async (submitMode = false, newStatus = 1, isAutosave = false) => {
            try {
                if (!isAutosave) setIsSaving(true);
                const values = isAutosave ? form.getFieldsValue() : await form.validateFields();
                console.log('Nilai dari Form:', values);
                const statusValueToSend = submitMode ? newStatus : (values.status === 'Draft' ? 1 : (values.status === 'Ready' ? 7 : values.status));
                console.log('Status yang akan dikirim:', statusValueToSend, typeof statusValueToSend);

                const dataToSave = {
                    ...values,
                    pr_date: values.pr_date?.format('YYYY-MM-DD'),
                    expected_arrival: values.expected_arrival?.format('YYYY-MM-DD HH:mm'),
                    items: items,
                    created_by: userData?.nama || 'System',
                    status: statusValueToSend,
                };
                console.log('Data final untuk Backend:', dataToSave);


                if (currentPrId) {
                    await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/purchase-requests/${currentPrId}`, dataToSave);
                    if (!isAutosave) notification.success({ message: 'Success', description: submitMode ? 'Data berhasil disubmit' : 'Data berhasil diperbarui', duration: 3 })
                    if (submitMode) {
                        setIsSubmitted(true);
                        form.setFieldsValue({ status: 'Ready' });
                    }
                } else {
                    if (submitMode && !isAutosave) {
                        notification.error({ message: 'Error', description: 'Tidak dapat submit PR baru, simpan sebagai draft terlebih dahulu.', duration: 3 });
                        if (!isAutosave) setIsSaving(false);
                        return;
                    }
                    dataToSave.pr_no = form.getFieldValue('pr_no');
                    const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/purchase-requests`, dataToSave);
                    if (!isAutosave) notification.success({ message: 'Success', description: 'Data berhasil disimpan', duration: 3 });

                    if (response.data?.id) {
                        setCurrentPrId(String(response.data.id));
                        navigate(`/dashboard/purchase_request/edit/${response.data.id}`, { replace: isAutosave });
                    }
                }

                if (isAutosave) {
                    setAutosaveStatus('idle');

                    setLastSavedValues(values);
                    setLastSavedItems([...items]);
                }

            } catch (error: any) {
                console.error('Gagal menyimpan/submit data:', error);
                const errorMsg = error.response?.data?.error || (submitMode ? 'Gagal submit data' : 'Gagal menyimpan data');
                if (!isAutosave && error.name !== 'ValidationError') notification.error({ message: 'Error', description: errorMsg, duration: 3 });
                if (isAutosave) setAutosaveStatus('error');
            } finally {
                if (!isAutosave) setIsSaving(false);
            }
        };

        const handleAutosave = async () => {
            const currentStatus = form.getFieldValue('status');
            if ((currentStatus !== 'Draft' && currentStatus !== 1) || isSubmitted) {
                setAutosaveStatus('stopped');
                return;
            }

            const currentValues = form.getFieldsValue(true);
            const currentItems = items;
            const valuesChanged = JSON.stringify(currentValues) !== JSON.stringify(lastSavedValues);
            const itemsChanged = JSON.stringify(currentItems) !== JSON.stringify(lastSavedItems);

            if (!valuesChanged && !itemsChanged) {
                setAutosaveStatus('idle');
                return;
            }
            setAutosaveStatus('processing');
            try {
                await handleSave(false, 1, true);

            } catch (error) {

            }
        };



        const confirmSave = () => {
            Modal.confirm({
                title: isEditMode ? 'Konfirmasi Pembaruan' : 'Konfirmasi Penyimpanan',
                content: 'Apakah data sudah benar?',
                okText: isEditMode ? 'Ya, Perbarui' : 'Ya, Simpan',
                cancelText: 'Batal',
                onOk: () => handleSave(false, 1)
            });
        };

        const handleSubmitPurchaseRequest = async () => {
            await handleSave(true, 7);
        };

        const confirmSubmit = () => {
            if (!isEditMode || !currentPrId) {
                notification.warning({ message: 'Warning', description: 'Simpan data sebagai draft terlebih dahulu sebelum submit.', duration: 3 });
                return;
            }
            const hasEmptyQty = items.some(item => !item.quantity || Number(item.quantity) <= 0);
            if (hasEmptyQty) {
                notification.error({ message: 'Error', description: 'Gagal Submit: Terdapat item dengan Qty kosong atau nol.', duration: 3 });
                return;
            }
            Modal.confirm({
                title: 'Konfirmasi Submit Purchase Request',
                content: 'Apakah Anda yakin ingin submit PR ini? Status akan berubah menjadi "Ready" dan tidak dapat diubah lagi.',
                okText: 'Ya, Submit',
                cancelText: 'Batal',
                onOk: handleSubmitPurchaseRequest
            });
        };

        const handleVoidPurchaseRequest = async () => {
            if (!currentPrId) {
                notification.error({ message: 'Error', description: 'Tidak ada Purchase Request yang dipilih untuk di-void.', duration: 3 });
                return;
            }
            setIsSaving(true);
            try {
                await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/purchase-requests/${currentPrId}`);
                const isStatus7 = statusWatchedValue === 'Ready' || statusWatchedValue === 7 || statusWatchedValue === '7';
                notification.success({
                    message: 'Success',
                    description: isStatus7 ? 'Purchase Request berhasil dibatalkan.' : 'Purchase Request berhasil di-void.',
                    duration: 3
                });
                navigate('/dashboard/browser_purchase_request');
            } catch (error: any) {
                console.error('Gagal melakukan void:', error);
                const errorMsg = error.response?.data?.error || 'Gagal melakukan void pada Purchase Request.';
                notification.error({ message: 'Error', description: errorMsg, duration: 3 });
            } finally {
                setIsSaving(false);
            }
        };

        const confirmVoid = () => {
            if (!isEditMode || !currentPrId) {
                notification.warning({ message: 'Warning', description: 'Tidak dapat melakukan void pada PR yang belum disimpan.', duration: 3 });
                return;
            }
            const isStatus7 = statusWatchedValue === 'Ready' || statusWatchedValue === 7 || statusWatchedValue === '7';
            Modal.confirm({
                title: isStatus7 ? 'Apakah yakin membatalkan purchase request?' : 'Konfirmasi Void Purchase Request',
                content: isStatus7 ? 'proses ini tidak dapat di batalkan!' : 'Apakah Anda yakin ingin membatalkan (void) PR ini? Tindakan ini akan menghapus data secara permanen dan tidak dapat diurungkan.',
                okText: isStatus7 ? 'Ya, Batalkan' : 'Ya, Void',
                cancelText: 'Batal',
                okButtonProps: { danger: true },
                onOk: handleVoidPurchaseRequest
            });
        };

        const handleNew = () => {
            form.resetFields();
            setItems([]);
            setSelectedRowKeys([]);
            setCurrentPrId(undefined);
            setIsSubmitted(false);
            setHasInitialSaveOccurred(false);
            form.setFieldsValue({
                status: 'Draft',
                dealer_rep_code: userData?.warehouse_code
            });
            navigate('/dashboard/purchase_request');
        };

        const handleImportItems = (importedItems: any[]) => {
            const warehouseCode = form.getFieldValue('dealer_rep_code');
            setItems(prevItems => {
                const updatedItems = [...prevItems];
                importedItems.forEach(newItem => {
                    const isDuplicate = updatedItems.some(item => item.item_code === newItem.item_code);
                    if (!isDuplicate) {
                        updatedItems.push({
                            ...newItem,
                            warehouse: warehouseCode,
                            key: newItem.item_code
                        });
                    }
                });
                return updatedItems;
            });
        };


        const handleEditItem = (updatedItem: any) => {
            setItems(items.map(item =>
                item.item_code === updatedItem.item_code ? updatedItem : item
            ));
        };
        const handleRowDoubleClick = (record: any) => {
            if (isSubmitted) return;
            setEditItem(record);
            setIsModalVisible(true);
        };
        const handleAddItem = (newItem: any) => {
            const isDuplicate = items.some(item => item.item_code === newItem.item_code);
            if (isDuplicate) {
                notification.error({ message: 'Error', description: `Item dengan kode ${newItem.item_code} sudah ada.`, duration: 3 });
                return;
            }
            const warehouseCode = form.getFieldValue('dealer_rep_code');
            setItems([...items, { ...newItem, warehouse: warehouseCode, key: newItem.item_code }]);
            setIsModalVisible(false);
        };
        const handleRowSelection = (selectedKeys: React.Key[], selectedRows: any[]) => {
            setSelectedRowKeys(selectedKeys);
        };
        const handleDelete = () => {
            if (selectedRowKeys.length === 0) {
                return;
            }
            setItems(items.filter(item => !selectedRowKeys.includes(item.item_code)));
            setSelectedRowKeys([]);
        };
        const shouldShowRemark = false;
        const [tableColumns] = useState<TableColumn[]>([
            { title: 'Item Code', dataIndex: 'item_code' },
            { title: 'Item Name', dataIndex: 'item_name' },
            { title: 'Qty', dataIndex: 'quantity' },
            { title: 'UoM', dataIndex: 'uom' },
            { title: 'UoM Rate', dataIndex: 'uom_rate' },
            ...(shouldShowRemark ? [{ title: 'Remark', dataIndex: 'remark_table_pr' }] : [])
        ]);


        const fetchDealers = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/dealers`);
                const options = response.data.map((warehouse: any) => ({
                    value: warehouse.warehouse_code,
                    label: warehouse.warehouse_name,
                }));
                setDealerOptions(options);
            } catch (error) {
                console.error('Gagal mengambil data warehouse:', error);
            }
        };

        const getLabelFromOptions = (value: string, options: { value: string; label: string }[]) => {
            const option = options.find(opt => opt.value === value);
            return option ? option.label : value;
        };

        const handlePrintReport = () => {
            const prNo = form.getFieldValue('pr_no');
            if (!prNo || !currentPrId) {
                notification.warning({ message: 'Warning', description: 'Silakan simpan Purchase Request terlebih dahulu sebelum mencetak.', duration: 3 });
                return;
            }
            Modal.confirm({
                title: 'Konfirmasi',
                content: 'Cetak dokument?',
                okText: 'Ya',
                cancelText: 'Tidak',
                onOk: async () => {
                    setIsPrinting(true);
                    const key = 'print_progress_notification';
                    const updateProgress = (percent: number) => {
                        notification.info({
                            key,
                            message: 'Mempersiapkan Dokumen',
                            description: <Progress percent={percent} status="active" />,
                            duration: 0,
                            placement: 'topRight',
                        });
                    };
                    try {
                        updateProgress(0);
                        const response = await axios.get<PurchaseRequestDetailData>(`${process.env.REACT_APP_BACKEND_URL}/api/purchase-requests/${currentPrId}`);
                        const prData = response.data;
                        updateProgress(20);
                        const reportDate = dayjs();
                        const formattedReportDate = reportDate.format('DD MMMM YYYY');
                        const dealerName = dealerOptions.find(d => d.value === prData.dealer_rep_code)?.label || prData.dealer_rep_code;
                        const itemGroupLabel = getLabelFromOptions(prData.item_group, [
                            { value: "001", label: "Inventory" },
                            { value: "002", label: "Out Side Job" },
                        ]);
                        const profitCenterLabel = getLabelFromOptions(prData.profit_center, profitCenterData || []);
                        const totalQty = prData.items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
                        const itemsPerPage = 23;
                        const totalPages = Math.max(1, Math.ceil(prData.items.length / itemsPerPage));
                        const reportBaseUrl = `${process.env.PUBLIC_URL}/reports/PR`;
                        const htmlTemplateResponse = await fetch(`${reportBaseUrl}/index.html`);
                        let htmlContent = await htmlTemplateResponse.text();
                        const bootstrapCssResponse = await fetch(`${reportBaseUrl}/bootstrap/css/bootstrap.css`);
                        const bootstrapCss = await bootstrapCssResponse.text();
                        const styleCssResponse = await fetch(`${reportBaseUrl}/style.css`);
                        const styleCss = await styleCssResponse.text();
                        const imgResponse = await fetch(`${reportBaseUrl}/assets/amp.png`);
                        const imgBlob = await imgResponse.blob();
                        const imgDataUrl = await new Promise<string>((resolve) => {
                            const reader = new FileReader();
                            reader.onloadend = () => resolve(reader.result as string);
                            reader.readAsDataURL(imgBlob);
                        });
                        const duration = 4000;
                        const interval = 100;
                        const steps = duration / interval;
                        const increment = (100 - 20) / steps;
                        let currentPercent = 20;
                        for (let i = 0; i < steps; i++) {
                            await new Promise(resolve => setTimeout(resolve, interval));
                            currentPercent += increment;
                            updateProgress(Math.round(currentPercent));
                        }
                        notification.destroy();
                        htmlContent = htmlContent.replace(
                            '<link href="bootstrap/css/bootstrap.css" rel="stylesheet">',
                            `<style>${bootstrapCss}</style>`
                        );
                        htmlContent = htmlContent.replace(
                            '<link href="style.css" rel="stylesheet">',
                            `<style>${styleCss}</style>`
                        );
                        htmlContent = htmlContent.replace(
                            '<img src="assets/amp.png" width="140" height="60" style="margin-left: 574px; margin-top: 14px;"/>',
                            `<img src="${imgDataUrl}" width="140" height="60" style="margin-left: 574px; margin-top: 14px;"/>`
                        );
                        htmlContent = htmlContent.replace(/fields_dealer_rep_code/g, dealerName);
                        htmlContent = htmlContent.replace(/fields_item_group/g, itemGroupLabel);
                        htmlContent = htmlContent.replace(/fields_profit_center/g, profitCenterLabel);
                        htmlContent = htmlContent.replace(/fields_dealer_rep_co/g, dealerName);
                        htmlContent = htmlContent.replace(/fields_set_order/g, "");
                        htmlContent = htmlContent.replace(/fields_back_order/g, "");
                        htmlContent = htmlContent.replace(/fields_project_no/g, '-');
                        htmlContent = htmlContent.replace(/fields_doc_no/g, prData.pr_no);
                        htmlContent = htmlContent.replace(/fields_doc_date/g, formattedReportDate);
                        htmlContent = htmlContent.replace(/fields_reference_no/g, '-');
                        htmlContent = htmlContent.replace(/fields_no_source/g, '-');
                        htmlContent = htmlContent.replace(/fields_arrival/g, '-');
                        htmlContent = htmlContent.replace(/fields_page_of/g, `1 Page of ${totalPages.toString()}`);
                        htmlContent = htmlContent.replace(/fileds_total/g, totalQty.toString());
                        htmlContent = htmlContent.replace(/Sampit, dd mm yyyy/g, `Sampit, ${formattedReportDate}`);
                        const tableBodyPlaceholder = `<tbody> \n                    <tr> \n                        <td style="padding-top: 3px; padding-bottom: 3px;">1</td> \n                        <td style="padding-top: 3px; padding-bottom: 3px;">fields_item_code</td> \n                        <td style="padding-top: 3px; padding-bottom: 3px;">fields_item_name</td> \n                        <td style="padding-top: 3px; padding-bottom: 3px;">fields_chassis</td>\n                        <td style="padding-top: 3px; padding-bottom: 3px;">fields_qty</td>\n                        <td style="padding-top: 3px; padding-bottom: 3px;">fields_uom</td>\n                        <td style="padding-top: 3px; padding-bottom: 3px;">fields_remark</td> \n                    </tr>                     \n                </tbody>`;
                        let itemRowsHtml = '';
                        if (prData.items && prData.items.length > 0) {
                            itemRowsHtml = prData.items.map((item, index) => `
                          <tr>
                              <td style="padding-top: 5px; padding-bottom: 5px;">${index + 1}</td>
                              <td style="padding-top: 5px; padding-bottom: 5px;">${item.item_code || '-'}</td>
                              <td style="padding-top: 5px; padding-bottom: 5px;">${item.item_name || '-'}</td>
                              <td style="padding-top: 5px; padding-bottom: 5px;">-</td>
                              <td style="padding-top: 5px; padding-bottom: 5px;">${item.quantity || 0}</td>
                              <td style="padding-top: 5px; padding-bottom: 5px;">${item.uom || '-'}</td>
                              <td style="padding-top: 5px; padding-bottom: 5px;">${item.remark_table_pr || '-'}</td>
                          </tr>
                      `).join('');
                        } else {
                            itemRowsHtml = `
                          <tr>
                              <td colspan="7" style="padding-top: 5px; padding-bottom: 5px; text-align: center;">Tidak ada item</td>
                          </tr>
                      `;
                        }
                        htmlContent = htmlContent.replace(tableBodyPlaceholder, `<tbody>${itemRowsHtml}</tbody>`);
                        const newWindow = window.open('', '_blank');
                        if (newWindow) {
                            newWindow.document.write(htmlContent);
                            newWindow.document.close();
                            newWindow.document.title = `Purchase Request - ${prData.pr_no}`;
                            setTimeout(() => {
                                newWindow.print();
                            }, 750);
                        } else {
                            notification.error({ message: 'Error', description: 'Gagal membuka jendela baru. Pastikan pop-up diizinkan.', duration: 3 });
                        }
                    } catch (error) {
                        console.error('Error generating print report:', error);
                        notification.error({ message: 'Error', description: 'Gagal membuat laporan cetak.', duration: 3 });
                    } finally {
                        setIsPrinting(false);
                        notification.destroy(key);
                    }
                }
            });
        };

        useEffect(() => {
            fetchDealers();
            if (prIdFromUrl) {
                if (String(prIdFromUrl) !== String(currentPrId)) {
                    setCurrentPrId(prIdFromUrl);
                    fetchPurchaseRequestDetails(prIdFromUrl);
                }
            } else {
                form.setFieldsValue({
                    status: 'Draft',
                    dealer_rep_code: userData?.warehouse_code
                });
                setCurrentPrId(undefined);
                setLastSavedValues(form.getFieldsValue(true));
                setLastSavedItems([]);
                setIsLoading(false);
            }
        }, [prIdFromUrl, userData, currentPrId]);

        useEffect(() => {
            const timeoutId = setTimeout(() => {
                const currentStatus = form.getFieldValue('status');
                const isExistingPr = !!currentPrId;
                if ((hasInitialSaveOccurred || isExistingPr) && (currentStatus === 'Draft' || currentStatus === 1)) {
                    handleAutosave();
                } else if ((currentStatus !== 'Draft' && currentStatus !== 1) || isSubmitted) {
                    setAutosaveStatus('stopped');
                }
            }, 1500);

            return () => clearTimeout(timeoutId);
        }, [allFormValues, items, hasInitialSaveOccurred, currentPrId, statusWatchedValue, isSubmitted]);

        const isVoidDisabled = !isEditMode || (isSubmitted && statusWatchedValue !== 'Ready' && statusWatchedValue !== 7 && statusWatchedValue !== '7');
         const displayedDealerOptions = dealerOptions;

        return (
            <>
                <WrappedComponent {...props}
                    form={form}
                    dealerOptions={displayedDealerOptions}
                    columns={tableColumns}
                    items={items}
                    showModal={showModal}
                    isModalVisible={isModalVisible}
                    onCancelModal={handleCancel}
                    onAddItem={handleAddItem}
                    onRowSelection={handleRowSelection}
                    selectedRowKeys={selectedRowKeys}
                    onDelete={handleDelete}
                    onEditItem={handleEditItem}
                    editItem={editItem}
                    onRowDoubleClick={handleRowDoubleClick}
                    onSave={handleSave}
                    isSaving={isSaving}
                    confirmSave={confirmSave}
                    isLoadingData={isLoading}
                    confirmSubmit={confirmSubmit}
                    isSubmitted={isSubmitted}
                    brandOptions={brandData || []}
                    profitCenterOptions={profitCenterData || []}
                    orderTypeOptions={orderTypeData || []}
                    onItemGroupChange={handleItemGroupChange}
                    autosaveStatus={autosaveStatus}
                    handlePrintReport={handlePrintReport}
                    confirmVoid={confirmVoid}
                    isVoidDisabled={isVoidDisabled}
                    statusValue={statusWatchedValue}
                    onNew={handleNew}
                    isPrinting={isPrinting}
                    onImportItems={handleImportItems}
                />
                <AddLinePr
                    visible={isModalVisible}
                    onClose={handleCancel}
                    addItem={handleAddItem}
                    editItem={editItem}
                    onEditItem={handleEditItem}
                    disabled={isSubmitted}
                    dealerRepCode={dealerRepCodeValue}
                />
            </>
        );
    };
};

export default withPurchaseRequestLogic;